/**
 * Helpers utilitaires pour le canvas — fonctions pures, sans dépendance React.
 * Extraits de LabelCanvas.jsx pour faciliter les tests unitaires et la
 * réutilisation par les composants Layer (TextLayer, ImageLayer, etc.).
 */

// 1pt typographique = 0.3528 mm. Dans le canvas, 1 unité = 1 mm.
// Pour convertir une taille en "pt" vers l'unité du canvas (mm) :
//   sizeInMm = sizePt * PT_TO_MM
export const PT_TO_MM = 0.3528;

/**
 * Génère la valeur CSS d'un dégradé linéaire ou radial à partir des stops.
 * @param {object} gradient - { type: 'linear'|'radial', angle?, stops: [{color, position}] }
 * @returns {string} - CSS background-image (ou 'none' si gradient invalide)
 */
export const getCssGradient = (gradient) => {
    if (!gradient || !gradient.stops || gradient.stops.length === 0) return 'none';
    const sortedStops = [...gradient.stops].sort((a, b) => a.position - b.position);
    const stopsStr = sortedStops.map(s => `${s.color} ${s.position}%`).join(', ');
    if (gradient.type === 'radial') {
        return `radial-gradient(circle at center, ${stopsStr})`;
    }
    return `linear-gradient(${gradient.angle || 0}deg, ${stopsStr})`;
};

/**
 * Calcule les paramètres CSS d'une ombre portée à partir des champs Photoshop
 * d'un élément (shadowAngle, shadowDistance, shadowSize, shadowSpread,
 * shadowOpacity, shadowColor, shadowInset).
 *
 * Convention Photoshop :
 *   X = cos(180 - angle) × distance       (180-angle pour aligner haut=270°)
 *   Y = sin(180 - angle) × distance
 *   CSS spread-radius = Size × Spread%
 *   CSS blur-radius   = Size - spread-radius
 *
 * Les longueurs renvoyées sont exprimées dans l'unité du canvas (le mm), la
 * même que la géométrie du calque : c'est le `transform: scale(canvas.scale)`
 * du sous-arbre qui les met à l'échelle de l'écran, ou MM_TO_CSSPX à l'export.
 *
 * @param {object} el - Élément du canvas
 * @returns {object|null} - { offsetX, offsetY, cssBlur, cssSpread, rgba, inset }
 *                          ou null si l'ombre n'est pas activée.
 */
export const getShadowParams = (el) => {
    if (!el.shadowEnabled) return null;

    // AVANT : `const s = scale / 3`, appliqué aux décalages comme aux tailles.
    // Or le sous-arbre du calque est DÉJÀ mis à l'échelle par
    // `transform: scale(canvas.scale)` : le zoom était donc compté deux fois et
    // l'ombre grossissait au carré du zoom. Son rapport à l'élément changeait à
    // chaque cran, et l'aperçu écran ne valait que pour le zoom auquel le projet
    // avait été enregistré. Les longueurs vivent maintenant dans l'unité du
    // canvas et le zoom n'est appliqué qu'une fois, par le transform.
    // Au zoom par défaut (canvas.scale = 3, où scale / 3 valait déjà 1) le rendu
    // est identique : les réglages déjà faits dans l'inspecteur gardent leur
    // aspect, et écran et PDF s'accordent désormais à tous les zooms.

    // 1) Angle → offsets (PS convention)
    const psAngle = el.shadowAngle !== undefined ? el.shadowAngle : 120;
    const radians = (180 - psAngle) * Math.PI / 180;
    const dist = el.shadowDistance !== undefined ? el.shadowDistance : 5;
    const offsetX = Math.round(Math.cos(radians) * dist * 10) / 10;
    const offsetY = Math.round(Math.sin(radians) * dist * 10) / 10;

    // 2) Size & Spread → blur + spread-radius
    const size = el.shadowSize !== undefined ? el.shadowSize : 5;
    const spreadPct = (el.shadowSpread !== undefined ? el.shadowSpread : 0) / 100;
    const cssSpread = Math.round(size * spreadPct * 10) / 10;
    const cssBlur = Math.round(Math.max(0, size - cssSpread) * 10) / 10;

    // 3) Couleur + opacité
    const opacity = (el.shadowOpacity !== undefined ? el.shadowOpacity : 75) / 100;
    let hex = el.shadowColor || '#000000';
    if (/^#[0-9a-fA-F]{3}$/.test(hex)) hex = '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
    if (!/^#[0-9a-fA-F]{6}$/.test(hex)) hex = '#000000';
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const rgba = `rgba(${r},${g},${b},${opacity})`;

    return { offsetX, offsetY, cssBlur, cssSpread, rgba, inset: el.shadowInset };
};

/**
 * Génère le style CSS d'une ombre portée pour les éléments NON-texte
 * (images, shapes, barcodes). Pour le texte on utilise text-shadow inline
 * dans le rendu (calculé directement via getShadowParams).
 *
 * @param {object} el - Élément du canvas
 * @returns {object} - Objet style CSS { filter: 'drop-shadow(...)' } ou {}
 */
export const getShadowStyle = (el) => {
    if (el.type === 'text') return {}; // text uses text-shadow
    const p = getShadowParams(el);
    if (!p) return {};

    // AVANT : le drapeau `inset` était calculé puis jamais lu, si bien qu'une
    // forme réglée sur « Interne » recevait quand même une ombre EXTERNE — le
    // bouton de l'inspecteur s'allumait sans rien changer au rendu. Une ombre
    // interne n'est pas un drop-shadow : elle est rendue par getInsetShadowStyle.
    if (p.inset) return {};

    // Toutes les shapes sont rendues en SVG → drop-shadow (suit le contour).
    // drop-shadow ne supporte pas spread, on combine blur + spread en un seul blur.
    const combinedBlur = p.cssBlur + p.cssSpread;
    return { filter: `drop-shadow(${p.offsetX}px ${p.offsetY}px ${combinedBlur}px ${p.rgba})` };
};

/**
 * Style CSS d'une ombre INTERNE (mode « Interne » de l'inspecteur) pour les
 * éléments NON-texte. À poser sur un calque de recouvrement placé AU-DESSUS du
 * contenu : un `box-shadow: inset` posé sur le conteneur lui-même est peint
 * sous ses enfants, donc masqué par toute image ou tout fond opaque.
 *
 * Contrairement à drop-shadow, box-shadow accepte le spread : on garde les
 * deux rayons calculés par getShadowParams.
 *
 * @param {object} el - Élément du canvas
 * @returns {object} - { boxShadow: 'inset ...' } ou {}
 */
export const getInsetShadowStyle = (el) => {
    if (el.type === 'text') return {}; // text-shadow n'a pas de variante interne
    const p = getShadowParams(el);
    if (!p || !p.inset) return {};
    return { boxShadow: `inset ${p.offsetX}px ${p.offsetY}px ${p.cssBlur}px ${p.cssSpread}px ${p.rgba}` };
};
